import { Injectable, inject } from '@angular/core';
import {
  MouseMovementHelperService,
  SnapInformation,
} from '../../../services/mouse-movement-helper.service';
import { Store } from '@ngxs/store';
import {
  DEFAULT_ACTIVITY_SNAPPING_MINUTES,
  DragAndDropModeService,
  HorizontalDemarcatorRange,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import { DragAndDropHelperService } from '../../../services/drag-and-drop-helper.service';
import { ActivityDropZoneHelperService } from '../../activity-drop-zone/activity-drop-zone-helper.service';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { firstValueFrom } from 'rxjs';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
} from '@wilson/interfaces';
import {
  ShiftTimelineDataState,
  ShiftTimelineState,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';

@Injectable()
export class ResizeActivityHelperService {
  private readonly mouseMovementHelperService = inject(
    MouseMovementHelperService,
  );
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly activitiesService = inject(ActivitiesGateway);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  getActualSnapInfo(
    offsetX: number,
    snappingInformation: SnapInformation[],
  ): { actualSnapPx: number; snappedDate: Date | null } {
    const actualSnapInfo: { actualSnapPx: number; snappedDate: Date | null } = {
      actualSnapPx: offsetX,
      snappedDate: null,
    };

    if (snappingInformation.length) {
      const snapInfo = this.mouseMovementHelperService.findSnapInfo(
        offsetX,
        snappingInformation,
      );
      if (snapInfo !== null) {
        actualSnapInfo.actualSnapPx = snapInfo.snapPx;
        actualSnapInfo.snappedDate = new Date(snapInfo.snapDateTime);
        this.dragAndDropModeService.setCustomSnappingStepSize(1);
        this.mouseMovementHelperService.setSnapPx(
          snapInfo.snapPx + DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        );
      } else {
        this.dragAndDropModeService.setCustomSnappingStepSize(
          DEFAULT_ACTIVITY_SNAPPING_MINUTES,
        );
        this.mouseMovementHelperService.setSnapPx(null);
      }
    }

    return actualSnapInfo;
  }

  async generateSnappingInformation(
    activities: Activity[],
    activityId: string | null,
  ): Promise<SnapInformation[]> {
    const snappingStepSize = await firstValueFrom(
      this.dragAndDropModeService.snappingStepSize$,
    );
    return this.mouseMovementHelperService.generateSnappingInformation(
      activities,
      activityId,
      snappingStepSize,
    );
  }

  resetDemarcatorLine(): void {
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
    this.dragAndDropModeService.toggleDurationOnDemarcatorLine(false);
    this.dragAndDropModeService.setDisplayTimeVisible(false);
    this.dragAndDropModeService.setIsMovingItemLeftRight(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
  }

  updateDemarcatorLine(
    currentDemarcatorRange: HorizontalDemarcatorRange,
  ): void {
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
    this.dragAndDropModeService.toggleDurationOnDemarcatorLine(true);
    this.dragAndDropModeService.setDisplayTimeVisible(true);
    this.dragAndDropModeService.setIsMovingItemLeftRight(true);
    this.dragAndDropModeService.setCustomSnappingStepSize(
      DEFAULT_ACTIVITY_SNAPPING_MINUTES,
    );
    this.dragAndDropModeService.setHorizontalDemarcatorRange(
      currentDemarcatorRange,
    );
  }

  showGapFillAnimation(
    dateTime: string,
    snappingStepSize: number,
    userId: string,
  ): void {
    this.activityDropZoneHelperService.setUserId(userId);
    this.mouseMovementHelperService.setAnimationOffsetX(
      this.dragAndDropHelperService.calculatePxFromTime({
        time: dateTime,
        snappingStepSize,
      }),
    );
    setTimeout(() => {
      this.mouseMovementHelperService.setAnimationOffsetX(null);
      this.activityDropZoneHelperService.setUserId(null);
    }, 1500);
  }

  async updateLeftHandleActivity(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    userId: string | null,
    dateToUpdate: Date,
  ): Promise<void> {
    if (activity.shiftId && userId) {
      try {
        await this.activitiesService.updateActivity({
          id: activity.id,
          startDatetime: dateToUpdate.toISOString(),
        });
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          userId,
        ]);
      } catch {
        this.store.dispatch(
          new TimelineUpsertActivityV2([activity], activity.shiftId),
        );
      }
    }
  }

  determineActivityDetails(
    dateToUpdate: Date,
    activities: Activity[],
    activity: Activity,
    accurateShiftStartDateTime: string,
    accurateShiftEndDateTime: string,
  ): {
    activityIndex: number;
    isActivityWithinShiftBounds: boolean;
  } {
    const activityIndex = activities.findIndex((a) => a.id === activity.id);
    const isActivityWithinShiftBounds = this.isActivityWithinShiftBounds(
      accurateShiftStartDateTime,
      accurateShiftEndDateTime,
      dateToUpdate,
    );
    return { activityIndex, isActivityWithinShiftBounds };
  }

  async updateRightHandleActivity(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    userId: string | null,
    dateToUpdate: Date,
  ): Promise<void> {
    if (activity.shiftId && userId) {
      try {
        await this.activitiesService.updateActivity({
          id: activity.id,
          endDatetime: dateToUpdate.toISOString(),
        });
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          userId,
        ]);
      } catch {
        this.store.dispatch(
          new TimelineUpsertActivityV2([activity], activity.shiftId),
        );
      }
    }
  }

  getDateToUpdate(currentOffsetX: number): Date {
    const timeframe = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    );
    const frameLength = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );
    const dateToUpdate = getDateFromPixelPosition({
      pixelPosition: currentOffsetX,
      timeframe,
      frameLength: frameLength,
    });
    return dateToUpdate;
  }

  isActivityWithinShiftBounds(
    accurateShiftStartDateTime: string,
    accurateShiftEndDateTime: string,
    dateToUpdate: Date,
  ): boolean {
    const newDate = dateToUpdate.toISOString();

    return (
      accurateShiftStartDateTime < newDate && accurateShiftEndDateTime > newDate
    );
  }
}
